// Panels from the Manus patient dashboard on the live patient page (approved 2026-11-02). They
// show what the hospital systems recorded, in neutral words. The risk, score, trajectory and
// contributor cards show the Manus scenario's predefined values on the synthetic site only
// (DemoValuePanels, approved 2026-11-03), and "not configured" on any other site.

import { SOURCE_LABELS, type EventSource } from '@diagnuvo/domain';
import { useEffect, useRef, type ReactNode } from 'react';
import type { EncounterView } from '../api.ts';
import { clockTime, displayName, triageText, zoneLabel } from '../board-state.ts';
import { ClockIcon, DatabaseIcon, HeartIcon, InfoIcon } from '../icons.tsx';
import {
  demoStages,
  demoValueLabel,
  latestEvent,
  liveContributors,
  NOT_CONFIGURED,
  pendingResults,
  physiologyText,
  PROGNOSTIC_HORIZONS,
} from '../patient-panels.ts';
import { STAGES, TOTAL_STAGES } from '../scenario/stages.ts';
import { Card } from './Layout.tsx';
import { directionText, TrajectoryChart } from './TrajectoryChart.tsx';

const sourceLabel = (s: string) => SOURCE_LABELS[s as EventSource] ?? s;

const fourHourText = (view: EncounterView): string => {
  const timer = view.timers.find((t) => t.id === 'ed-4h');
  if (timer === undefined) return 'Timer not started.';
  const recorded =
    timer.alert === 'breach'
      ? 'Breach recorded'
      : timer.alert === 'warning'
        ? 'Warning recorded'
        : 'No alert recorded';
  return timer.stoppedAt === undefined
    ? recorded
    : `${recorded} · stopped at ${clockTime(timer.stoppedAt)}`;
};

const pendingText = (view: EncounterView): string => {
  const { labs, imaging } = pendingResults(view.labs, view.imaging);
  if (labs + imaging === 0) return 'No lab or imaging result is waiting.';
  return `${labs} lab and ${imaging} imaging result${labs + imaging === 1 ? '' : 's'} waiting.`;
};

/** Where the patient is, in plain words, from the recorded state. */
export function recordedState(view: EncounterView): string {
  const { state } = view;
  const status =
    state.leftEdAt !== undefined
      ? 'Left the ED'
      : state.status === 'discharged'
        ? 'Discharged'
        : state.status === 'cancelled'
          ? 'Arrival cancelled'
          : state.status === 'admitted'
            ? 'Admitted, awaiting a bed'
            : state.status === 'arrived'
              ? 'In the ED'
              : 'Not yet arrived';
  const place = [zoneLabel(state.location?.room), state.location?.bed].filter(Boolean).join(' · ');
  return place === '' ? status : `${status} · ${place}`;
}

export function SituationSummary({ view }: { view: EncounterView }) {
  const last = latestEvent(view.timeline, sourceLabel);
  return (
    <Card
      title="Clinical situation summary"
      icon={<InfoIcon />}
      info="The visit in one place: the latest record time, the recorded state, what changed and which system sent it."
    >
      <dl className="summary-list" data-testid="situation">
        <dt>Latest record time</dt>
        <dd>{last?.at ?? '—'}</dd>
        <dt>Current recorded state</dt>
        <dd>{recordedState(view)}</dd>
        <dt>What changed</dt>
        <dd>{last?.what ?? 'Nothing recorded yet.'}</dd>
        <dt>Why the display changed</dt>
        <dd>{last === undefined ? '—' : `New record from ${last.source}`}</dd>
      </dl>
    </Card>
  );
}

export function DynamicState({ view }: { view: EncounterView }) {
  return (
    <Card
      title="Dynamic clinical state"
      icon={<HeartIcon />}
      info="The latest recorded observations and how they changed since the set before. Observation only."
    >
      <p data-testid="physiology">{physiologyText(view.observations, view.previousObservations)}</p>
      <p className="note">Recorded values only. Not a clinical assessment.</p>
    </Card>
  );
}

/** A card whose output Diagnuvo does not produce for live patients: title and the boundary only. */
function NotConfiguredCard({
  title,
  icon,
  testId,
}: {
  title: string;
  icon: ReactNode;
  testId: string;
}) {
  return (
    <Card title={title} icon={icon} info={NOT_CONFIGURED.text}>
      <p className="output-value not-configured" data-testid={testId}>
        {NOT_CONFIGURED.value}
      </p>
      <p className="note">{NOT_CONFIGURED.text}</p>
    </Card>
  );
}

export function NotConfiguredPanels() {
  return (
    <>
      <div className="grid-2">
        <NotConfiguredCard title={NOT_CONFIGURED.riskTitle} icon={<InfoIcon />} testId="risk" />
        <NotConfiguredCard
          title={NOT_CONFIGURED.scoreTitle}
          icon={<InfoIcon />}
          testId="priority-score"
        />
      </div>
      <div className="grid-2">
        <NotConfiguredCard title="Risk trajectory" icon={<ClockIcon />} testId="trajectory" />
        <NotConfiguredCard title="Major contributors" icon={<HeartIcon />} testId="contributors" />
      </div>
      <Card title="Prognostic information" icon={<DatabaseIcon />} info={NOT_CONFIGURED.text}>
        <ul className="sources" data-testid="prognostic">
          {PROGNOSTIC_HORIZONS.map((label) => (
            <li key={label}>
              <span>{label}</span>
              <span className="muted">Not configured</span>
            </li>
          ))}
        </ul>
        <p className="note">{NOT_CONFIGURED.text}</p>
      </Card>
    </>
  );
}

/**
 * Risk, Priority Score, trajectory and contributors with the #/demo stage values, for a site in
 * DEMO_VALUE_SITES (synthetic data only). The stage comes from recorded milestones; every value
 * carries its "predefined, not calculated from this patient" label. Prognosis stays not
 * configured: Manus and the handoff pack give no values for it.
 */
export function DemoValuePanels({ view }: { view: EncounterView }) {
  const reached = demoStages(view.state.milestones);
  const current = reached.at(-1);
  if (current === undefined) {
    return (
      <Card title="Predefined demonstration values" icon={<InfoIcon />}>
        <p className="muted" data-testid="risk">
          No arrival recorded yet: no demonstration stage.
        </p>
      </Card>
    );
  }
  const { stage } = current;
  const label = demoValueLabel(stage);
  const index = reached.length - 1;
  return (
    <>
      <div className="grid-2">
        <OutputCard
          title={NOT_CONFIGURED.riskTitle}
          value={`${stage.risk}%`}
          share={stage.risk / 100}
          label={label}
          testId="risk"
        />
        <OutputCard
          title={NOT_CONFIGURED.scoreTitle}
          value={`${stage.priorityScore} / 100`}
          share={stage.priorityScore / 100}
          label={label}
          testId="priority-score"
        />
      </div>
      <div className="grid-2">
        <Card title="Risk trajectory" icon={<ClockIcon />} info={label}>
          <p className="output-label">Predefined demonstration values</p>
          <TrajectoryChart
            stages={STAGES}
            index={index}
            total={TOTAL_STAGES}
            times={reached.map((r) => clockTime(r.at))}
          />
          <p className="traj-direction" data-testid="trajectory-direction">
            {directionText(STAGES, index)}
          </p>
          <p className="note">
            Stages follow this visit's recorded milestones: arrival, first observations, first lab
            result, observations after it. {label}
          </p>
        </Card>
        <Card title="Major contributors" icon={<HeartIcon />} info={label}>
          <ul className="sources" data-testid="contributors">
            {liveContributors(stage, view.observations).map((c) => (
              <li key={c.key} data-contributor={c.key}>
                <span>
                  {c.label} {c.value ?? ''}
                </span>
                <span className="pill pill-muted">{c.tag}</span>
              </li>
            ))}
          </ul>
          <p className="note">
            The signals of Manus scenario Stage {stage.number}, with this patient's latest values as
            sent. No feature weights are shown.
          </p>
        </Card>
      </div>
      <Card title="Prognostic information" icon={<DatabaseIcon />} info={NOT_CONFIGURED.text}>
        <ul className="sources" data-testid="prognostic">
          {PROGNOSTIC_HORIZONS.map((h) => (
            <li key={h}>
              <span>{h}</span>
              <span className="muted">Not configured</span>
            </li>
          ))}
        </ul>
        <p className="note">The demonstration scenario has no prognostic values.</p>
      </Card>
    </>
  );
}

function OutputCard({
  title,
  value,
  share,
  label,
  testId,
}: {
  title: string;
  value: string;
  share: number;
  label: string;
  testId: string;
}) {
  return (
    <Card title={title} icon={<InfoIcon />} info={label}>
      <p className="output-label">Predefined demonstration value</p>
      <p className="output-value" data-testid={testId}>
        {value}
      </p>
      <span className="bar" aria-hidden="true">
        <span className="bar-fill" style={{ width: `${share * 100}%` }} />
      </span>
      <p className="note">{label}</p>
    </Card>
  );
}

/**
 * The visit at a glance for a clinician, from the same encounter view as the page (live or
 * replayed). Risk and score only as the labelled predefined values (synthetic site), never a
 * prognosis. Escape, the backdrop or the button closes it.
 */
export function ClinicianView({
  view,
  replaying,
  demo,
  onClose,
}: {
  view: EncounterView;
  replaying: boolean;
  /** The site shows the predefined demonstration values (DEMO_VALUE_SITES). */
  demo: boolean;
  onClose: () => void;
}) {
  const stage = demo ? demoStages(view.state.milestones).at(-1)?.stage : undefined;
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    close.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const obs = view.observations;
  const recent = view.timeline.slice(-3).reverse();
  return (
    <div className="cv-backdrop" onClick={onClose}>
      <section
        className="cv"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cv-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv-head">
          <div>
            <h2 id="cv-title">CLINICIAN VIEW</h2>
            <p className="muted">
              {displayName(view.state.patient)} · {recordedState(view)}
              {view.asOf === undefined ? '' : ` · as of ${clockTime(view.asOf)}`}
            </p>
          </div>
          <span className="badge badge-demo">{replaying ? 'Replay' : 'Live record'}</span>
        </header>
        <div className="cv-section">
          <h3>Reason for attendance and triage</h3>
          <p>
            {view.state.complaint?.text ?? 'No presenting complaint recorded.'} ·{' '}
            {view.state.triage === undefined
              ? 'Not triaged yet'
              : `${triageText(view.state.triage)} (${clockTime(view.state.triage.at)})`}
          </p>
        </div>
        <div className="cv-section">
          <h3>Current dynamic clinical state</h3>
          <p>{physiologyText(obs, view.previousObservations)}</p>
        </div>
        <div className="cv-section">
          <h3>Current recorded observations</h3>
          {obs === undefined ? (
            <p className="muted">No observations recorded yet.</p>
          ) : (
            <>
              <p className="obs-line">
                {obs.values
                  .map((v) => `${v.code === 'NEWS2' ? 'NEWS2 (as sent)' : v.label} ${v.value}`)
                  .join(' · ')}
              </p>
              <p className="source">
                {clockTime(obs.at)} · Observations system
                {obs.device === undefined ? '' : ` (${obs.device})`}
              </p>
            </>
          )}
        </div>
        <div className="cv-section">
          <h3>Most recent clinical changes</h3>
          {recent.length === 0 ? (
            <p className="muted">Nothing recorded yet.</p>
          ) : (
            <ul className="sources">
              {recent.map((item) => (
                <li key={item.id}>
                  <span>
                    {clockTime(item.at)} · {item.title}
                    {item.detail === undefined ? '' : ` · ${item.detail}`}
                  </span>
                  <span className="muted">{sourceLabel(item.source)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="cv-section">
          <h3>Data availability</h3>
          <p>
            {view.sources.length === 0
              ? 'No system has sent data for this visit yet.'
              : `Received from: ${view.sources.map((s) => s.label).join(', ')}.`}
          </p>
          <p className="muted">{pendingText(view)}</p>
        </div>
        <div className="cv-section">
          <h3>ED 4-hour standard</h3>
          <p>{fourHourText(view)}</p>
        </div>
        <div className="cv-section">
          <h3>
            {NOT_CONFIGURED.riskTitle} · {NOT_CONFIGURED.scoreTitle}
          </h3>
          {stage === undefined ? (
            <p className="muted">
              {NOT_CONFIGURED.value}. {NOT_CONFIGURED.text}
            </p>
          ) : (
            <>
              <p>
                {NOT_CONFIGURED.riskTitle}: <strong>{stage.risk}%</strong> ·{' '}
                {NOT_CONFIGURED.scoreTitle}: <strong>{stage.priorityScore} / 100</strong>
              </p>
              <p className="muted">{demoValueLabel(stage)}</p>
            </>
          )}
        </div>
        <footer className="cv-foot">
          <button ref={close} type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </footer>
      </section>
    </div>
  );
}
